// Injects @augmodo/reusable-components' stylesheet, side-effect only —
// `import "./inject-reusable-components-css"` once at app entry.
//
// Imported ?raw and injected by hand: the package ships Tailwind v4-compiled
// CSS whose bare `@layer` rules break this app's Tailwind 3 postcss pass, so
// it must bypass the CSS pipeline. All its rules are `au:`-prefixed and
// token-scoped, so injecting globally can't clash with app styles.
import reusableComponentsCss from "@augmodo/reusable-components/styles.css?raw";

/* global CSSLayerStatementRule, CSSLayerBlockRule -- newer DOM classes missing
   from ESLint's browser-globals list */
// Unwrap the package CSS's @layer blocks before injecting. This app's own
// Tailwind 3 styles are unlayered, and unlayered CSS beats layered CSS
// regardless of specificity — so e.g. v3 preflight's `*{border-width:0}` was
// overriding the package's layered `au:border` utilities (borders vanished
// from the viewer toolbar). Flattening the layers restores normal specificity;
// the file's rule order already matches the intended layer order.
const flattenCssLayers = (css: string): string => {
  const sheet = new CSSStyleSheet();
  sheet.replaceSync(css);
  const serialize = (rules: CSSRuleList): string =>
    Array.from(rules)
      .map((rule) => {
        if (rule instanceof CSSLayerStatementRule) return "";
        if (rule instanceof CSSLayerBlockRule) return serialize(rule.cssRules);
        // Drop the package's one universal-selector rule (a Tailwind v4
        // `*{--tw-*: initial}` fallback for pre-@property browsers). It's the
        // only rule in the file that can touch app elements: 13 of its --tw-*
        // names are also used by this app's Tailwind 3, and in old
        // Safari/Firefox (where its @supports guard matches) it would
        // override v3's defaults and break app shadows/rings. Modern browsers
        // never apply it, so dropping costs nothing there; old browsers get a
        // slightly degraded viewer instead of a broken app.
        if (
          rule instanceof CSSStyleRule &&
          /(^|,)\s*\*/.test(rule.selectorText)
        )
          return "";
        if (rule instanceof CSSMediaRule)
          return `@media ${rule.conditionText}{${serialize(rule.cssRules)}}`;
        if (rule instanceof CSSSupportsRule)
          return `@supports ${rule.conditionText}{${serialize(rule.cssRules)}}`;
        return rule.cssText;
      })
      .join("\n");
  return serialize(sheet.cssRules);
};

const reusableComponentsStyles = document.createElement("style");
reusableComponentsStyles.textContent = flattenCssLayers(reusableComponentsCss);
document.head.appendChild(reusableComponentsStyles);
